import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { AuthGuardService } from './auth-team/services/AuthGuard';

// ROUTING TO TEAM DASHBOARD PAGES

export function hyphenatedBusinessUrlMatcher(
  segments: UrlSegment[]
): UrlMatchResult | null {
  // Check if there is exactly one segment in the path (e.g., 'holiday-season-animal')
  if (segments.length === 1) {
    const fullSegment = segments[0].path;
    const staticPrefix = 'pooper-scooper-';

    // Check if the segment starts with the required static prefix
    if (fullSegment.startsWith(staticPrefix)) {
      // 1. Extract the business name (the part after the hyphen)
      const businessName = fullSegment.substring(staticPrefix.length);

      // 2. Return the match result
      return {
        // Specify the entire segment as the consumed path
        consumed: segments,
        // Define the parameters we've extracted
        posParams: {
          businessName: new UrlSegment(businessName, {}),
        },
      };
    }
  }

  // If no match, return null
  return null;
}

const routes: Routes = [
  { path: '', redirectTo: '/home', pathMatch: 'full' },
  {
    path: 'home',
    loadChildren: () => import('./home-team/home.module').then(m => m.HomeModule),
  },
{ 
    // Use 'matcher' instead of 'path'
    matcher: hyphenatedBusinessUrlMatcher, 
    // pathMatch: 'full' is not needed when using a custom matcher
    loadChildren: () => import('./home-team/home.module').then(m => m.HomeModule),
  },
  {
    path: 'features',
    loadChildren: () => import('./home-team/features/features.module').then(m => m.FeaturesModule),
  },
  {
    path: 'terms-of-service',
    loadChildren: () => import('./home-team/terms-of-service/terms-of-service.module').then(m => m.TermsOfServiceModule),
  },
  {
    path: 'privacy-policy',
    loadChildren: () => import('./home-team/privacy-policy/privacy-policy.module').then(m => m.PrivacyPolicyModule),
  },
  {
    path: 'global-dashboard',
    loadChildren: () => import('./dashboard-team/dashboard/dashboard.module').then(m => m.DashboardModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'ai-agent-onboarding',
    loadChildren: () => import('./onboarding-team/business-onboarding/ai-agent-onboarding.module').then(m => m.AiAgentOnboardingModule),
  },
    {
    path: 'quote-chat',
    loadChildren: () => import('./onboarding-team/user-onboarding/quote-chat.module').then(m => m.QuoteChatModule),
  },
  {
    path: 'example',
    loadChildren: () => import('./dashboard-team/example/example.module').then(m => m.ExampleModule),
  },
  {
    path: 'accessible-pages',
    loadChildren: () => import('./accessible-pages/accesiblepages.module').then(m => m.AccesiblePagesModule),
  },
  {
    path: 'settings',
    loadChildren: () => import('./auth-team/settings/settings.module').then(m => m.SettingsModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'login',
    loadChildren: () => import('./auth-team/login/login.module').then(m => m.LoginModule)
  },
  {
    path: 'forgot',
    loadChildren: () => import('./auth-team/forgot-password/forgot.module').then(m => m.ForgotModule)
  },
  {
    path: 'contact-us',
    loadChildren: () => import('./nav-team/contact-us/contactus.module').then(m => m.ContactUsModule)
  },
  {
    path: 'user-profiles',
    loadChildren: () => import('./auth-team/all-user-profiles/all-user-profiles.module').then(m => m.AllUserProfilesModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'user-access-rights',
    loadChildren: () => import('./auth-team/access-rights/access-rights.module').then(m => m.AccessRightsModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'user-profiles-inactive',
    loadChildren: () => import('./auth-team/all-user-profiles-inactive/all-user-profiles-inactive.module').then(m => m.AllUserProfilesInactiveModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'zerobusinesses',
    loadChildren: () => import('./auth-team/zero-businesses/zerobusinesses.module').then(m => m.ZeroBusinessesModule),
  },
  {
    path: 'jobdashboard',
    loadChildren: () => import('./job-team/job-dashboard/jobdashboard.module').then(m => m.JobDashboardModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'clientportal',
    loadChildren: () => import('./role-specific-portals/client-portal-team/clientportal.module').then(m => m.ClientPortalModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'technicianportal',
    loadChildren: () => import('./role-specific-portals/tech-portal-team/techportal.module').then(m => m.TechPortalModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'recurringjobs',
    loadChildren: () => import('./job-team/job-team-component-lib/job-item/job-item.module').then(m => m.JobItemModuleModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'findjobs',
    loadChildren: () => import('./job-team/job-search/jobsearch.module').then(m => m.JobSearchModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'job',
    loadChildren: () => import('./job-team/job-view-page/job-view-page.module').then(m => m.JobViewPageModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'invoice',
    loadChildren: () => import('./invoice-team/invoice-view/invoice-view.module').then(m => m.InvoiceViewModule),
    canActivate : [AuthGuardService]
  },
  // {
  //   path: 'myupcomingjobs',
  //   loadChildren: () => import('./job-team/job-my-upcoming/myupcoming.module').then(m => m.MyUpcomingModule),
  //   canActivate : [AuthGuardService]
  // },
  {
    path: 'invoices',
    loadChildren: () => import('./invoice-team/invoice-dashboard/invoice-dashboard.module').then(m => m.InvoiceDashboardModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'apihealth',
    loadChildren: () => import('./api-health-team/apihealth.module').then(m => m.ApiHealthDashboardModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'start-onboarding',
    loadChildren: () => import('./auth-team/stripe-onboarding/startonboarding.module').then(m => m.StartOnboardingModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'stripe-connection-status',
    loadChildren: () => import('./auth-team/stripe-finished-onboarding/finishedonboarding.module').then(m => m.FinishedOnboardingModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'square-onboarding',
    loadChildren: () => import('./auth-team/square-onboarding/square-onboarding.module').then(m => m.SquareOnboardingModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'square-connection-status',
    loadChildren: () => import('./auth-team/square-finished-onboarding/square-finished-onboarding.module').then(m => m.SquareFinishedOnboardingModule),
    canActivate : [AuthGuardService]
  },
  {
    path: 'terms-of-service',
    loadChildren: () => import('./auth-team/terms-of-service/tos.module').then(m => m.TocModule),
  },
  {
    path: 'about',
    loadChildren: () => import('./nav-team/about-us/aboutus.module').then(m => m.AboutUsModule),
  },
  {
    path: 'docs',
    loadChildren: () => import('./nav-team/docs/docs.module').then(m => m.DocsModule),
  }

];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
